import type { Annotation } from "building-types/annotation/annotationTypes";
import type { DataTable } from "building-types/dataTables/dataTable";
import type { Doc } from "building-types/doc";
import type { ContentTemplateHandle, ContentTemplateInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { ParameterValue, ParameterValueHandle } from "building-types/options/parameter";
import { type Parameter, type ParameterRef } from "building-types/options/parameter";
import type {
  SheetParameterValues,
  SystemParameterValues,
  ViewportParameterValues,
} from "building-types/systemParameterTypes";
import type { PlanSetTemplateConfig, Viewport } from "building-types/views/planSets";
import {
  ViewportScalingMethod,
  WRAPPER_CONTENT_TEMPLATE_INSTANCE_ID,
  type ContentTemplate,
  type ContentTemplateInstance,
  type DrawingTemplateInstance,
  type Evaluated,
  type EvaluatedDataTable,
  type EvaluatedViewport,
  type PlanSetTemplate,
} from "building-types/views/planSets";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import degreeToRadian from "common/utils/degreeToRadian";
import objectFilter from "common/utils/objectFilter";
import objectMap from "common/utils/objectMap";
import {
  contentTemplateAllowsViewportOrDataTable,
  isSystemContentTemplateId,
  isViewportTitleDrawingTemplate,
} from "doc/contentTemplateTools";
import { docGetContentTemplate } from "doc/docGetContentTemplates";
import {
  docEntityGetChildrenFromTable,
  docEntityGetSortedChildrenFromTable,
} from "doc/entity/docEntityGetChildrenFromTable";
import { DocEntityNodeRegistry } from "doc/entity/docEntityNodeRegistry";
import { docGetEntityGroup } from "doc/entity/docGetGroup";
import { evaluateAnnotationParameterRefs } from "doc/parameterAnnotation";
import { evaluateParameterDefaultValues } from "doc/parameterCoreTools";
import type { ParameterReplacementValueContext } from "doc/parameterTools";
import {
  evaluateDisabledField,
  evaluateFormulasParameterRefsInObject,
  evaluateParameterBindings,
  getParameterReplacementValue,
  evaluateParameterValueHandles,
  parameterValuesMerge,
} from "doc/parameterTools";
import {
  docCollectPlanSetTemplateParameterValues,
  docGetPlanSetTemplateIdentifierConfig,
} from "doc/planSetTemplateTools";
import { viewportParameters } from "doc/systemParameters";
import { systemParametersFromDocWithoutBuilding } from "doc/systemParameterTools";
import { formatViewScaleFactor } from "doc/viewScaleFormat";
import type { Mat3 } from "geom/mat3";
import { mat3Ident, mat3Mult, mat3Rotate, mat3Translate } from "geom/mat3";
import { polyLine2AABB2 } from "geom/polyLine2";
import { drawingTemplateInstanceNew } from "./contentTemplateInstanceWrapper";
import { getSystemContentTemplateAnnotations } from "./getSystemContentTemplateAnnotations";
import { sheetParameterValuesFromSheet } from "./sheetParameterValuesFromSheet";

export function evaluateTemplateArguments(
  unevaluatedTemplateArguments: Table<ParameterValueHandle | ParameterValue | ParameterRef>,
  templateParameters: Table<Parameter>,
  systemParameterValues: Table<ParameterValue>,
  parentScope: Table<ParameterValue | ParameterRef>,
): Table<ParameterValue | ParameterRef> {
  return {
    ...systemParameterValues,
    ...evaluateParameterDefaultValues(templateParameters),
    ...evaluateParameterBindings(unevaluatedTemplateArguments, {
      ...systemParameterValues,
      ...parentScope,
    }),
  };
}

const VIEWPORT_TITLE_PARAM_KEYS = ["description", "title", "number"] as const;
const EMPTY_VIEWPORT_PARAMETER_VALUES: ViewportParameterValues = {
  viewportDescription: "",
  viewportName: "",
  viewportNumber: "",
  viewportScale: "",
};

export interface EvaluatePlanSetTemplateInstanceWithoutBuildingOptions {
  doc: Doc;
  template: PlanSetTemplate;
  config?: PlanSetTemplateConfig;
  globalParameterValues: Table<ParameterValue>;
  templateParameterValues?: Table<ParameterValue>;
  userDefinedOptionChoices?: OptionChoices;
  shallow?: boolean;
  entityFilter?: EntityFilterFn;
}

export function planSetTemplateGlobalParameterValues(
  doc: Doc,
  systemParameters: Partial<SystemParameterValues>,
  template: PlanSetTemplate,
  planSetTemplateConfig?: PlanSetTemplateConfig,
  userDefinedOptionChoices?: OptionChoices,
): Table<ParameterValue> {
  // Get the global params that are available to this template
  const { systemParameterDefaultValues } = systemParametersFromDocWithoutBuilding(doc, userDefinedOptionChoices);
  const globalParamDefaultValues = evaluateParameterDefaultValues(doc.globalParameters);

  planSetTemplateConfig =
    planSetTemplateConfig ?? docGetPlanSetTemplateIdentifierConfig(doc, { type: "PLAN_SET_TEMPLATE", id: template.id });

  const configGlobalParameterValues = planSetTemplateConfig
    ? evaluateParameterValueHandles(planSetTemplateConfig.globalParameterValues)
    : {};

  // Merge the global param default values with the ones passed in.
  // systemParameters is last so that caller-provided overrides take precedence.
  return {
    ...systemParameterDefaultValues,
    ...globalParamDefaultValues,
    ...configGlobalParameterValues,
    ...systemParameters,
  };
}

/**
 * PlanSetTemplates, like ContentTemplates, are like function definitions and this function is responsible for evaluating one.
 * Similar to evaluating a function call in an interpreted language, we first evaluate the arguments to the function using the
 * parent scope and then we evaluate the function body.
 * This function only evaluates the parameters that don't need a generated building. Those parameters that need a building,
 * will be substituted with a value later at geometry generation stage.
 */
export function evaluatePlanSetTemplateInstanceWithoutBuilding({
  doc,
  template,
  config,
  globalParameterValues,
  templateParameterValues,
  userDefinedOptionChoices,
  shallow,
  entityFilter,
}: EvaluatePlanSetTemplateInstanceWithoutBuildingOptions): PlanSetTemplateInstanceNode {
  // Get the local parameters for this template
  const localParameters = docEntityGetChildrenFromTable(doc.localParameters, template.id);

  const planSetTemplateConfig =
    config ?? docGetPlanSetTemplateIdentifierConfig(doc, { type: "PLAN_SET_TEMPLATE", id: template.id });

  const planSetTemplateParameterValues =
    templateParameterValues ?? // Primarily used for test assertions
    docCollectPlanSetTemplateParameterValues(
      doc,
      { type: "PLAN_SET_TEMPLATE", id: template.id },
      { type: "CUSTOM", templateId: template.id, templateConfigId: planSetTemplateConfig?.id },
    );

  // Evaluate the arguments
  const evaluatedArguments = evaluateTemplateArguments(
    planSetTemplateParameterValues,
    localParameters,
    globalParameterValues,
    {},
  );

  // Evaluate body
  const children = docEntityGetSortedChildrenFromTable(doc.contentTemplateInstances, template.id);

  const sheets: RWTable<ContentTemplateInstanceNode> = {};

  for (const unevaluatedSheet of children) {
    const sheet = evaluateContentTemplateInstance({
      instance: unevaluatedSheet,
      doc,
      globalParamValues: globalParameterValues,
      parentParams: localParameters,
      parentScope: evaluatedArguments,
      transform: mat3Ident(),
      userDefinedOptionChoices,
      shallow,
      entityFilter,
    });

    if (sheet.instance.disabled === true) {
      continue;
    }

    sheets[sheet.instance.id] = sheet;
  }

  return {
    template,
    sheets,
  };
}

export interface PlanSetTemplateInstanceNode {
  readonly template: PlanSetTemplate;
  readonly sheets: Table<ContentTemplateInstanceNode>;
}

export interface ContentTemplateInstanceNodeChildren {
  readonly viewports: Table<EvaluatedViewport>;
  readonly dataTables: Table<EvaluatedDataTable>;
  readonly annotation: Table<Annotation>;
  readonly childInstances: Table<ContentTemplateInstanceNode>;
}

export interface ContentTemplateInstanceNodeBase {
  readonly sheetTransform: Mat3;
  readonly instance: ContentTemplateInstance;
  readonly template: ContentTemplate;
  readonly instanceChildren: ContentTemplateInstanceNodeChildren;
  readonly templateChildren: ContentTemplateInstanceNodeChildren;
}

/**
 * A denormalized ContentTemplateInstance with all its ParameterRefs evaluated.
 */

export type ContentTemplateInstanceNode = SheetTemplateInstanceNode | DrawingTemplateInstanceNode;

export interface SheetTemplateInstanceNode extends ContentTemplateInstanceNodeBase {
  readonly templateType: "SHEET";
}

export interface DrawingTemplateInstanceNode extends ContentTemplateInstanceNodeBase {
  readonly templateType: "DRAWING";
}

export type EntityFilterFn = (entity: Viewport | DataTable | Annotation) => boolean;

export interface EvaluateContentTemplateInstanceOptions {
  instance: ContentTemplateInstance;
  doc: Doc;
  globalParamValues?: Table<ParameterValue>;
  parentParams?: Table<Parameter>;
  parentScope?: Table<ParameterValue | ParameterRef>;
  transform?: Mat3;
  userDefinedOptionChoices?: OptionChoices;
  shallow?: boolean;
  filterDisabledEntities?: boolean;
  entityFilter?: EntityFilterFn;
  visitedInstanceIds?: Set<string>;
}

/**
 * Recursively converts a ContentTemplateInstance to a tree structure
 * and evaluates ParameterRefs and replaces them with their values. It also
 * computes any transforms and stores them on the node.
 * @param filterDisabledEntities - if true, disabled child entities of the instance will be filtered
 */
export function evaluateContentTemplateInstance({
  instance,
  doc,
  globalParamValues = {},
  parentParams = {},
  parentScope = {},
  transform = mat3Ident(),
  userDefinedOptionChoices,
  shallow = false,
  filterDisabledEntities = true,
  entityFilter,
  visitedInstanceIds = new Set(),
}: EvaluateContentTemplateInstanceOptions): ContentTemplateInstanceNode {
  // Look up the template for this instance
  const template = docGetContentTemplate(doc, { type: "CONTENT_TEMPLATE", id: instance.contentTemplateId });

  assertIsNotUndefined(template);

  const contentTemplateParams = docEntityGetChildrenFromTable(doc.localParameters, instance.contentTemplateId);

  // Get the system params that are available to this template
  const { systemParameters, systemParameterDefaultValues } = systemParametersFromDocWithoutBuilding(
    doc,
    userDefinedOptionChoices,
  );

  const sheetParamValues: Partial<SheetParameterValues> =
    instance.templateType === "SHEET" && instance.id !== WRAPPER_CONTENT_TEMPLATE_INSTANCE_ID
      ? sheetParameterValuesFromSheet(instance)
      : {};

  // collect viewport parameter defaults for editing a viewport title template
  const viewportParameterValues: Partial<ViewportParameterValues> =
    isViewportTitleDrawingTemplate(template) && instance.id === WRAPPER_CONTENT_TEMPLATE_INSTANCE_ID
      ? {
          viewportName: template.viewportName,
          viewportNumber: template.viewportNumber,
          viewportDescription: template.viewportDescription,
          viewportScale: template.viewportScale,
        }
      : {};

  const globalParamDefaultValues = evaluateParameterDefaultValues(doc.globalParameters);

  globalParamValues = {
    ...systemParameterDefaultValues,
    ...globalParamDefaultValues,
    ...globalParamValues,
    ...sheetParamValues,
    ...viewportParameterValues,
  };

  // Evaluate the arguments
  const evaluatedParameterValues = evaluateTemplateArguments(
    instance.parameterValues,
    contentTemplateParams,
    globalParamValues,
    parentScope,
  );

  const instanceScope: Table<ParameterValue | ParameterRef> = { ...parentScope, ...evaluatedParameterValues };

  // Disabled is like an argument as it has access to the parent scope
  const instanceEvaluated = evaluateFormulasParameterRefsInObject(instance, instanceScope);

  if (instanceEvaluated.disabled || shallow) {
    return contentTemplateInstanceNodeNew(instanceEvaluated, template);
  }

  let childTransform = transform;

  if (template.templateType === "DRAWING") {
    const { x, y, rotation } = instance as DrawingTemplateInstance;

    const rot = mat3Rotate(degreeToRadian(rotation ?? 0));
    const trans = mat3Translate(x, y);

    // Compose the child's local transform (rotate about its own origin, then translate) first,
    // then the accumulated ancestor transform.
    childTransform = mat3Mult(transform, mat3Mult(trans, rot));
  }

  // Nested content template instances can form cycles. Stop expanding once the
  // current traversal path reaches the same instance again.
  if (visitedInstanceIds.has(instance.id)) {
    return contentTemplateInstanceNodeNew(instanceEvaluated, template, childTransform);
  }

  const nextVisitedInstanceIds = new Set(visitedInstanceIds);
  nextVisitedInstanceIds.add(instance.id);

  const instanceParams: Table<Parameter> = {
    ...parentParams,
    ...systemParameters,
    ...doc.globalParameters,
    ...contentTemplateParams,
  };

  function evaluateTable<T extends Viewport | Annotation | DataTable>(table: Table<T>): Table<Evaluated<T>> {
    const evaluatedTable = evaluateFormulasParameterRefsInObject(table, instanceScope) as Table<Evaluated<T>>;

    if (!filterDisabledEntities) {
      return evaluatedTable;
    }

    return objectFilter(evaluatedTable, (e) => {
      const entity = e as T;

      if (entity.disabled) {
        return false;
      }

      if (entityFilter && !entityFilter(entity)) {
        return false;
      }

      const group = docGetEntityGroup(doc, { type: entity.entityType, id: entity.id });

      if (!group) {
        return true;
      }

      return !evaluateDisabledField(group.disabled, instanceScope);
    });
  }

  function evaluateChildInstanceTable(table: Table<ContentTemplateInstance>): Table<ContentTemplateInstanceNode> {
    const nodes = objectMap(table, (child) =>
      evaluateContentTemplateInstance({
        instance: child,
        doc,
        globalParamValues,
        parentParams: instanceParams,
        parentScope: instanceScope,
        transform: childTransform,
        userDefinedOptionChoices,
        shallow,
        filterDisabledEntities,
        entityFilter,
        visitedInstanceIds: nextVisitedInstanceIds,
      }),
    );

    if (filterDisabledEntities) {
      return objectFilter(nodes, (e) => !e.instance.disabled);
    }

    return nodes;
  }

  const emptyChildren: ContentTemplateInstanceNodeChildren = {
    viewports: {},
    dataTables: {},
    annotation: {},
    childInstances: {},
  };

  function evaluateInstanceChildren(parentHandle: ContentTemplateInstanceHandle | ContentTemplateHandle) {
    const parentId = parentHandle.id;

    let childInstances: Table<ContentTemplateInstanceNode> = {};
    let viewports: Table<EvaluatedViewport> = {};
    let dataTables: Table<EvaluatedDataTable> = {};
    const unevaluatedAnnotation: RWTable<Annotation> = {};

    if (isSystemContentTemplateId(parentId)) {
      const systemAnnotations = getSystemContentTemplateAnnotations(parentId);
      for (const annotationId in systemAnnotations) {
        unevaluatedAnnotation[annotationId] = systemAnnotations[annotationId];
      }
    } else {
      const parentNode = DocEntityNodeRegistry.getNode(doc, parentHandle);
      if (!parentNode) {
        return emptyChildren;
      }
      let childInstanceTable: Table<ContentTemplateInstance> = Object.fromEntries(
        parentNode.getChildrenOfType("CONTENT_TEMPLATE_INSTANCE").map((child) => [child.getId(), child.getDocEntity()]),
      );
      if (template && contentTemplateAllowsViewportOrDataTable(template)) {
        // We avoid looking up viewports or data tables if they can't be contained in the instance as an optimization.
        const viewportsTable = Object.fromEntries(
          parentNode.getChildrenOfType("VIEWPORT").map((child) => [child.getId(), child.getDocEntity()]),
        ) as Table<Viewport>;
        viewports = evaluateTable(viewportsTable) as Table<EvaluatedViewport>;
        childInstanceTable = {
          ...generateViewportTitleTemplateInstances(
            doc,
            viewports,
            doc.viewports,
            doc.contentTemplates,
            instanceScope,
            instanceParams,
          ),
          ...childInstanceTable,
        };
        const dataTablesTable = Object.fromEntries(
          parentNode.getChildrenOfType("DATA_TABLE").map((child) => [child.getId(), child.getDocEntity()]),
        ) as Table<DataTable>;
        dataTables = evaluateTable(dataTablesTable) as Table<EvaluatedDataTable>;
      }
      childInstances = evaluateChildInstanceTable(childInstanceTable);

      const annotationNodes = parentNode.getChildrenOfType("ANNOTATION");
      for (const annotationNode of annotationNodes) {
        const annotation = annotationNode.getDocEntity();
        unevaluatedAnnotation[annotation.id] = annotation;
      }
    }

    const annotation: RWTable<Annotation> = objectMap(evaluateTable(unevaluatedAnnotation), (anno) =>
      evaluateAnnotationParameterRefs(anno, { doc, values: instanceScope, params: instanceParams }),
    );

    return {
      viewports,
      annotation,
      dataTables,
      childInstances,
    };
  }

  return contentTemplateInstanceNodeNew(
    instanceEvaluated,
    template,
    childTransform,
    // Only sheets can have children on the instance. This check can speed up generateAnnotation significantly.
    template.templateType === "SHEET"
      ? evaluateInstanceChildren({ type: "CONTENT_TEMPLATE_INSTANCE", id: instance.id })
      : emptyChildren,
    evaluateInstanceChildren({ type: "CONTENT_TEMPLATE", id: instance.contentTemplateId }),
  );
}

function contentTemplateInstanceNodeNew(
  instance: ContentTemplateInstance,
  template: ContentTemplate,
  sheetTransform = mat3Ident(),
  instanceChildren: ContentTemplateInstanceNodeChildren = {
    viewports: {},
    dataTables: {},
    annotation: {},
    childInstances: {},
  },
  templateChildren: ContentTemplateInstanceNodeChildren = {
    viewports: {},
    dataTables: {},
    annotation: {},
    childInstances: {},
  },
): ContentTemplateInstanceNode {
  if (instance.templateType === "SHEET") {
    return {
      templateType: instance.templateType,
      sheetTransform,
      instance,
      template,
      instanceChildren,
      templateChildren,
    };
  }

  return {
    templateType: instance.templateType,
    sheetTransform,
    instance,
    template,
    instanceChildren,
    templateChildren,
  };
}

/**
 * Evaluates a content template instance but looks up if the entity has a plan set
 * template parent. If so, it injects the plan set template parameter definitions
 * and its corresponding plan set template config parameter values.
 *
 * This function also ignores the disabled field of the instance since we want to
 * still view the sheet content even if the instance will be disabled on export.
 */
export function evaluateContentTemplateInstanceForEditorViewer({
  instance: templateInstance,
  doc,
  globalParamValues = {},
  filterDisabledEntities = true,
}: EvaluateContentTemplateInstanceOptions) {
  const instance = { ...templateInstance, disabled: false };

  const planSetTemplate = doc.planSetTemplates[instance.parentId ?? ""];
  const paramDefs = docEntityGetChildrenFromTable(doc.localParameters, planSetTemplate?.id);

  return evaluateContentTemplateInstance({
    instance,
    doc,
    globalParamValues,
    parentParams: paramDefs,
    parentScope: evaluateTemplateArguments({}, paramDefs, globalParamValues, {}),
    filterDisabledEntities,
  });
}

function evaluateViewportTitleParams(
  replacementValueContext: ParameterReplacementValueContext,
  viewport: EvaluatedViewport,
  unevaluatedViewport: Viewport,
) {
  const viewportWithResolvedParams = { ...viewport };
  // For Viewport Title parameters, remap from entity id to entity name
  for (const key of VIEWPORT_TITLE_PARAM_KEYS) {
    const parameterRef = unevaluatedViewport[key];

    if (typeof parameterRef !== "object") {
      continue;
    }
    viewportWithResolvedParams[key] = getParameterReplacementValue(parameterRef.id, replacementValueContext);
  }
  return viewportWithResolvedParams;
}

function generateViewportTitleTemplateInstances(
  doc: ParameterReplacementValueContext["doc"],
  viewports: Table<EvaluatedViewport>,
  unevaluatedViewports: Table<Viewport>,
  contentTemplates: Table<ContentTemplate>,
  instanceScope: Table<ParameterValue | ParameterRef>,
  instanceParams: Table<Parameter>,
): Table<ContentTemplateInstance> {
  const instances: RWTable<ContentTemplateInstance> = {};
  for (const viewportId in viewports) {
    const viewport = viewports[viewportId];
    const unevaluatedViewport = unevaluatedViewports[viewportId];
    if (!viewport.titleDrawingTemplateId) {
      continue;
    }

    const contentTemplate = docGetContentTemplate(
      { contentTemplates },
      { type: "CONTENT_TEMPLATE", id: viewport.titleDrawingTemplateId },
    );

    // validate that the content template is a viewport title template
    if (!contentTemplate || !isViewportTitleDrawingTemplate(contentTemplate)) {
      continue;
    }

    const { minX, minY } = polyLine2AABB2(viewport.outline);

    const viewportWithResolvedTitleParams = evaluateViewportTitleParams(
      { doc, values: instanceScope, params: instanceParams },
      viewport,
      unevaluatedViewport,
    );
    // Seed viewport title parameter values with empty strings to prevent rendering the placeholder parameter token in the plan.
    const viewportParameterDefaults = { ...EMPTY_VIEWPORT_PARAMETER_VALUES };
    parameterValuesMerge(viewportParameterDefaults, evaluateParameterDefaultValues(instanceParams));
    parameterValuesMerge(
      viewportParameterDefaults,
      evaluateParameterDefaultValues(viewportParameters(contentTemplate)),
    );
    parameterValuesMerge(viewportParameterDefaults, {
      ...getParameterValuesFromViewport(viewportWithResolvedTitleParams),
    });
    const { viewportName, viewportNumber, viewportDescription, viewportScale } = viewportParameterDefaults;

    const parameterValueHandles: Record<keyof ViewportParameterValues, ParameterValueHandle> = {
      viewportName: { type: "PRIMITIVE", value: viewportName },
      viewportNumber: { type: "PRIMITIVE", value: viewportNumber },
      viewportDescription: { type: "PRIMITIVE", value: viewportDescription },
      viewportScale: { type: "PRIMITIVE", value: viewportScale },
    };

    instances[viewport.id] = drawingTemplateInstanceNew({
      contentTemplateId: viewport.titleDrawingTemplateId,
      parameterValues: parameterValueHandles,
      parentId: viewport.id,
      x: minX,
      y: minY,
    });
  }

  return instances;
}

function getParameterValuesFromViewport(viewport: EvaluatedViewport): ViewportParameterValues {
  const defaults: ViewportParameterValues = {
    viewportName: viewport.title ?? viewport.name,
    viewportNumber: viewport.number ?? "",
    viewportDescription: viewport.description ?? "",
    viewportScale: "",
  };

  if (viewport.scalingMethod === ViewportScalingMethod.FIT || !viewport.scale) {
    return defaults;
  }

  return {
    ...defaults,
    viewportScale: formatViewScaleFactor(viewport.scale),
  };
}
